import { useEffect, useState } from 'react';
import { KeyRound, Save, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { adminAPI } from '../services/api.js';
import ErrorBanner from './ErrorBanner.jsx';

// OWNER-ONLY: per-tenant provider API key overrides (separate billing). The owner
// selects a tenant in the sidebar switcher, then pastes that tenant's OWN
// Anthropic (Claude) + ElevenLabs keys here. Keys are write-only — the server
// stores ciphertext and only ever returns a masked preview ("sk-…abcd") + a
// "set" flag, never the plaintext. Empty → the call falls back to the shared
// platform keys (the four prod tenants stay on shared, unchanged).
//
// Renders nothing for non-owners (the server also enforces this with a 403).
export default function TenantApiKeysEditor() {
  const { isOwner, selectedTenant } = useAuth();
  const tenantId = selectedTenant?.id;

  const [state, setState] = useState({ anthropic: { set: false, masked: null }, elevenLabs: { set: false, masked: null } });
  const [encryptionConfigured, setEncryptionConfigured] = useState(true);
  const [anthropicInput, setAnthropicInput] = useState('');
  const [elevenInput, setElevenInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminAPI.getTenantApiKeys();
      const data = res?.data || res || {};
      setState({
        anthropic: data.anthropic || { set: false, masked: null },
        elevenLabs: data.elevenLabs || { set: false, masked: null }
      });
      setEncryptionConfigured(data.encryptionConfigured !== false);
      setAnthropicInput('');
      setElevenInput('');
    } catch (err) {
      setError(err.message || 'Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever the owner switches tenant (so VentureOS shows VentureOS).
  useEffect(() => {
    if (isOwner) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, tenantId]);

  if (!isOwner) return null;

  const saveKey = async (field, value) => {
    setSaving(true);
    setError('');
    try {
      await adminAPI.updateTenantApiKeys({ [field]: value });
      setSavedAt(new Date());
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save API key.');
    } finally {
      setSaving(false);
    }
  };

  const KeyRow = ({ title, vendor, status, input, setInput, field, placeholder }) => (
    <div className="border border-stone-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-stone-100 font-medium">{title}</h3>
        {status.set ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-300">
            On {vendor}'s own account · {status.masked || 'set'}
          </span>
        ) : (
          <span className="text-xs px-2 py-0.5 rounded-full bg-stone-800 text-stone-400">
            Using shared platform key
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          type="password"
          autoComplete="new-password"
          className="field-input flex-1"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading || saving || !encryptionConfigured}
        />
        <button
          type="button"
          className="btn-primary"
          disabled={loading || saving || !encryptionConfigured || input.trim().length < 10}
          onClick={() => saveKey(field, input.trim())}
        >
          <Save size={14} />
          Save
        </button>
        {status.set && (
          <button
            type="button"
            className="btn-ghost"
            disabled={loading || saving}
            onClick={() => saveKey(field, null)}
            title="Clear this key (fall back to the shared platform key)"
          >
            <Trash2 size={14} />
            Clear
          </button>
        )}
      </div>
    </div>
  );

  return (
    <section className="card mb-8">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={18} className="text-alfred-gold" />
        <h2 className="text-xl text-stone-100">Provider API keys (owner)</h2>
      </div>
      <p className="text-sm text-stone-400 mb-4">
        Run <span className="text-stone-200">{selectedTenant?.companyName || 'this tenant'}</span> on
        its OWN Anthropic (Claude) and ElevenLabs accounts for separate billing. Leave
        blank to use the shared platform keys. Keys are encrypted at rest and never
        shown again after saving.
      </p>

      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} />
        </div>
      )}
      {!encryptionConfigured && (
        <div className="mb-4 text-sm text-amber-300 bg-amber-900/20 border border-amber-800 rounded p-3">
          Server encryption is not configured (APP_ENCRYPTION_KEY is unset) — keys
          cannot be stored until it is set.
        </div>
      )}

      <div className="space-y-4">
        <KeyRow
          title="Anthropic (Claude) API key"
          vendor="this tenant"
          status={state.anthropic}
          input={anthropicInput}
          setInput={setAnthropicInput}
          field="anthropicApiKey"
          placeholder="sk-ant-api03-…"
        />
        <KeyRow
          title="ElevenLabs API key"
          vendor="this tenant"
          status={state.elevenLabs}
          input={elevenInput}
          setInput={setElevenInput}
          field="elevenLabsApiKey"
          placeholder="sk_…"
        />
      </div>

      {savedAt && (
        <p className="mt-3 text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</p>
      )}
    </section>
  );
}
